import { Image as ImageIcon, CalendarClock, ShieldCheck, Medal } from "lucide-react";
import { TrustBar } from "../WhyUs/TrustBar";
import { MaskedSlideReveal } from "@/components/ui/masked-slide-reveal";
import { ShareloEmbed } from "@/components/ui/ShareloEmbed";


type Item = { Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; title: string; description: string };

const FREE_MOCKUP: Item = {
  Icon: ImageIcon,
  title: "Free Mockup",
  description: "We'd rather show you than pitch you. See the work first, pay later.",
};
const STRATEGY_CALL: Item = {
  Icon: CalendarClock,
  title: "Free Strategy Call",
  description: "30 minutes to map your goals, your brand, and your next move.",
};
const MONEY_BACK: Item = {
  Icon: Medal,
  title: "Senior-Level Work",
  description: "A Creative Director with 20 years of brand and web experience.",
};
const NO_CONTRACTS: Item = {
  Icon: ShieldCheck,
  title: "100% Money Back",
  description: "Not happy after round one? Full refund, no questions asked.",
};

function RiskItem({ Icon, title, description }: Item) {
  return (
    <div className="flex items-center gap-5 rounded-[30px] border border-foreground/10 bg-background/40 px-6 py-3">
      <div className="flex shrink-0 items-center justify-center">
        <Icon className="h-14 w-14 text-brand-blue" strokeWidth={1.75} />
      </div>
      <div className="min-w-0">
        <h4 className="text-[1.1rem] font-bold leading-tight text-foreground">{title}</h4>
        <p className="mt-2 text-[0.9rem] leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  );
}

interface RiskReversalCardProps {
  onCtaClick?: () => void;
}

export function RiskReversalCard({ onCtaClick }: RiskReversalCardProps) {
  return (
    <div className="mt-16 sm:mt-20">
      <div className="text-center">
            <h3 className="font-bold leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-5xl text-foreground">
              You See It Before<br className="sm:hidden" />{" "}
              <span className="bg-gradient-to-r from-brand-blue via-[#a855f7] to-[#ec4899] bg-clip-text text-transparent">
                You Pay Anything.
              </span>
            </h3>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              See your site design before<br className="sm:hidden" /> a dollar changes hands
            </p>
          </div>

          <div className="mt-10 grid gap-5 sm:gap-6 lg:grid-cols-[1fr_1.8fr_1fr]">
            <div className="order-1 lg:order-none flex flex-col justify-center gap-5 sm:gap-8">
              <RiskItem {...FREE_MOCKUP} />
              <RiskItem {...MONEY_BACK} />
            </div>

            <div className="order-3 lg:order-none flex w-full flex-col items-center">
              <ShareloEmbed id="dyr6DN2BEKowGy3DQkmeW" title="Risk reversal — customer story" className="w-full max-w-3xl" preload />
            </div>

            <div className="order-2 lg:order-none flex flex-col justify-center gap-5 sm:gap-8">
              <RiskItem {...STRATEGY_CALL} />
              <RiskItem {...NO_CONTRACTS} />
            </div>

            <div className="order-4 lg:order-none col-span-full mt-5 sm:mt-6 flex justify-center">
              <TrustBar onCtaClick={onCtaClick} showAvatars={false} />
            </div>

            <MaskedSlideReveal
              text="The only thing you're risking is 30 minutes of your time."
              className="order-5 lg:order-none col-span-full block w-full text-center text-lg sm:text-xl font-semibold"
              gradientClassName="bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent text-2xl sm:text-3xl font-bold"
            />
          </div>
        </div>
  );
}
